"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger> & {
  asChild?: boolean;
}) => (
  <PopoverPrimitive.Trigger
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </PopoverPrimitive.Trigger>
);
PopoverTrigger.displayName = "PopoverTrigger";

type PopoverContentProps = PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    "align" | "alignOffset" | "anchor" | "side" | "sideOffset"
  > &
  Pick<PopoverPrimitive.Portal.Props, "container"> & {
    keepMounted?: boolean;
    onCloseAutoFocus?: (event: { preventDefault: () => void }) => void;
    onEscapeKeyDown?: (event: KeyboardEvent) => void;
    onInteractOutside?: (event: PointerEvent) => void;
  };

const PopoverContent = ({
  className,
  align = "center",
  alignOffset = 0,
  container,
  side = "bottom",
  sideOffset = 4,
  keepMounted = false,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: PopoverContentProps) => {
  void onEscapeKeyDown;
  void onInteractOutside;
  const finalFocus = React.useMemo(() => {
    if (!onCloseAutoFocus) {
      return undefined;
    }

    return () => {
      let defaultPrevented = false;
      onCloseAutoFocus({
        preventDefault: () => {
          defaultPrevented = true;
        },
      });
      return defaultPrevented ? false : true;
    };
  }, [onCloseAutoFocus]);

  return (
    <PopoverPrimitive.Portal container={container} keepMounted={keepMounted}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-100"
      >
        <PopoverPrimitive.Popup
          finalFocus={finalFocus}
          className={(state) =>
            cn(
              `
                z-100 w-72
                rounded-md
                border
                bg-popover
                p-4
                text-muted-foreground
                shadow-md
                outline-hidden
                data-ending-style:animate-out
                data-ending-style:fade-out-0
                data-ending-style:zoom-out-95
                data-starting-style:animate-in
                data-starting-style:fade-in-0
                data-starting-style:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2
              `,
              resolveClassName(className, state),
            )
          }
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};
PopoverContent.displayName = "PopoverContent";

export { Popover, PopoverContent, PopoverTrigger };
